import { create } from 'zustand';
import type { AuthState, AuthStore, UserInfo, AuthErrorCode } from '@/types';
import { DEFAULT_TOKEN_REFRESH_CONFIG, keycloakService } from '../services/keycloak';
import { MockAuthService, isMockAuthEnabled } from '../services/mockAuth';
import { buildUserInfoFromTokenParsed } from '../services/claims';
import { refreshHubSpotEntitlements } from '../services/entitlements';
import { broadcastShellLogout } from '@/features/embedding/utils/logout';

/**
 * Initial auth state
 */
const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  accessToken: null,
  hubspotCustomerId: null,
  tokenExpiry: null,
  error: null,
  entitlementsRefreshed: false,
};

/**
 * Get mock auth service instance
 */
const getMockService = () => MockAuthService.getInstance();

const getKeycloakTokenExpiry = () => {
  const tokenParsed = keycloakService.getTokenParsed();
  return tokenParsed?.exp ? tokenParsed.exp * 1000 : null;
};

const stringArraysEqual = (left: string[] | undefined, right: string[] | undefined) => {
  const leftValues = left ?? [];
  const rightValues = right ?? [];
  const leftSet = new Set(leftValues);

  return (
    leftValues.length === rightValues.length
    && rightValues.every((value) => leftSet.has(value))
  );
};

const userInfoEqual = (left: UserInfo | null, right: UserInfo | null | undefined) => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    left.email === right.email
    && left.sub === right.sub
    && stringArraysEqual(left.roles, right.roles)
    && stringArraysEqual(left.permissions, right.permissions)
  );
};

const getTokenStateUpdate = (
  state: AuthStore,
  token: string,
  tokenExpiry: number | null,
  refreshedUser: UserInfo | null
): Partial<AuthState> | null => {
  const updates: Partial<AuthState> = {};

  if (!state.isAuthenticated) {
    updates.isAuthenticated = true;
  }

  if (state.isLoading) {
    updates.isLoading = false;
  }

  if (state.accessToken !== token) {
    updates.accessToken = token;
  }

  if (state.tokenExpiry !== tokenExpiry) {
    updates.tokenExpiry = tokenExpiry;
  }

  if (refreshedUser && !userInfoEqual(state.user, refreshedUser)) {
    updates.user = refreshedUser;
  }

  if (state.error !== null) {
    updates.error = null;
  }

  return Object.keys(updates).length > 0 ? updates : null;
};

const refreshLoginEntitlements = async (accessToken: string) => {
  const entitlementRefresh = await refreshHubSpotEntitlements(accessToken);
  if (!entitlementRefresh) {
    return {
      refreshed: false,
      hubspotCustomerId: null,
    };
  }

  if (!entitlementRefresh.enabled) {
    throw new Error('User disabled');
  }

  if (entitlementRefresh.tokenRefreshRequired) {
    const refreshedToken = await keycloakService.ensureValidToken(-1);
    if (!refreshedToken || refreshedToken === accessToken) {
      throw new Error('Failed to refresh token after entitlement update');
    }

    const refreshedEntitlements = await refreshHubSpotEntitlements(refreshedToken);
    if (refreshedEntitlements && !refreshedEntitlements.enabled) {
      throw new Error('User disabled');
    }

    return {
      refreshed: true,
      hubspotCustomerId: refreshedEntitlements?.hubspotCustomerId
        ?? entitlementRefresh.hubspotCustomerId,
    };
  }

  return {
    refreshed: true,
    hubspotCustomerId: entitlementRefresh.hubspotCustomerId,
  };
};

/**
 * Auth store implementation
 */
export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  initialize: async () => {
    set({ isLoading: true, error: null });
    
    // Use mock auth in development when enabled
    if (isMockAuthEnabled()) {
      console.log('[Auth] Using mock authentication service');
      const mockService = getMockService();
      const authenticated = await mockService.initialize();
      
      if (authenticated) {
        const user = mockService.getUser();
        set({
          isLoading: false,
          isAuthenticated: true,
          user,
          accessToken: mockService.getToken(),
          hubspotCustomerId: null,
          tokenExpiry: Date.now() + 3600000, // 1 hour from now
          entitlementsRefreshed: false,
        });
      } else {
        set({
          isLoading: false,
          isAuthenticated: false,
          user: null,
          accessToken: null,
          hubspotCustomerId: null,
          tokenExpiry: null,
          entitlementsRefreshed: false,
        });
      }
      return;
    }
    
    try {
      // Connect to Keycloak
      const authenticated = await keycloakService.connect((token) => {
        // Handle token refresh callback
        const keycloak = keycloakService.getKeycloak();
        if (keycloak && keycloak.tokenParsed) {
          const refreshedUser = buildUserInfoFromTokenParsed(keycloak.tokenParsed);
           set({
             accessToken: token,
             tokenExpiry: keycloak.tokenParsed.exp ? keycloak.tokenParsed.exp * 1000 : null,
             ...(refreshedUser ? { user: refreshedUser } : {})
           });
        }
      }, () => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          set({
            isLoading: true,
            error: {
              code: 'NETWORK_ERROR' as AuthErrorCode,
              message: 'Connection lost. Reconnecting your session...',
            },
          });
          return;
        }
        broadcastShellLogout();
        set({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          accessToken: null,
          hubspotCustomerId: null,
          tokenExpiry: null,
          entitlementsRefreshed: false,
          error: {
            code: 'TOKEN_EXPIRED' as AuthErrorCode,
            message: 'Session expired. Please sign in again.',
          },
        });
      });
      
      const keycloak = keycloakService.getKeycloak();

      if (authenticated && keycloak && keycloak.tokenParsed) {
        const initialToken = keycloak.token;
        const entitlementBootstrap = initialToken
          ? await refreshLoginEntitlements(initialToken)
          : { refreshed: false, hubspotCustomerId: null };
        const tokenParsed = keycloakService.getTokenParsed() ?? keycloak.tokenParsed;
        const user: UserInfo = buildUserInfoFromTokenParsed(tokenParsed) ?? {
          email: tokenParsed.email || '',
          sub: tokenParsed.sub,
          roles: [],
          permissions: [],
        };

        set({
          isLoading: false,
          isAuthenticated: true,
          user,
          accessToken: keycloakService.getToken() ?? keycloak.token ?? null,
          hubspotCustomerId: entitlementBootstrap.hubspotCustomerId,
          tokenExpiry: getKeycloakTokenExpiry(),
          entitlementsRefreshed: entitlementBootstrap.refreshed,
        });
      } else {
        set({
          isLoading: false,
          isAuthenticated: false,
          user: null,
          accessToken: null,
          hubspotCustomerId: null,
          tokenExpiry: null,
          entitlementsRefreshed: false
        });
      }
    } catch (error) {
      set({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        accessToken: null,
        hubspotCustomerId: null,
        tokenExpiry: null,
        entitlementsRefreshed: false,
        error: {
          code: 'INITIALIZATION_FAILED' as AuthErrorCode,
          message: error instanceof Error && error.message === 'User disabled'
            ? 'User disabled'
            : 'Failed to initialize authentication',
          originalError: error,
        },
      });
    }
  },

  login: async () => {
    set({ isLoading: true, error: null });
    
    // Use mock auth in development when enabled
    if (isMockAuthEnabled()) {
      const mockService = getMockService();
      await mockService.login();
      const user = mockService.getUser();
      set({
        isLoading: false,
        isAuthenticated: true,
        user,
        accessToken: mockService.getToken(),
        hubspotCustomerId: null,
        tokenExpiry: Date.now() + 3600000,
        entitlementsRefreshed: false,
      });
      return;
    }
    
    try {
      await keycloakService.login();
      // Login redirects, so execution might not continue here
    } catch (error) {
      set({
        isLoading: false,
        error: {
          code: 'LOGIN_FAILED' as AuthErrorCode,
          message: 'Login failed',
          originalError: error,
        },
      });
    }
  },

  logout: async () => {
    set({ isLoading: true, error: null });
    broadcastShellLogout();
    
    // Use mock auth in development when enabled
    if (isMockAuthEnabled()) {
      const mockService = getMockService();
      await mockService.logout();
      get().clearAuth();
      return;
    }
    
    try {
      await keycloakService.logout();
      get().clearAuth();
    } catch (error) {
      set({
        isLoading: false,
        error: {
          code: 'LOGOUT_FAILED' as AuthErrorCode,
          message: 'Logout failed',
          originalError: error,
        },
      });
    }
  },

  ensureValidToken: async (minValidity = DEFAULT_TOKEN_REFRESH_CONFIG.minValidity) => {
    if (isMockAuthEnabled()) {
      const mockService = getMockService();
      const token = mockService.getToken();
      set({
        isAuthenticated: mockService.getIsAuthenticated(),
        accessToken: token,
        hubspotCustomerId: null,
        tokenExpiry: token ? Date.now() + 3600000 : null,
        entitlementsRefreshed: false,
        error: null,
      });
      return token;
    }

    const token = await keycloakService.ensureValidToken(minValidity);

    if (token) {
      const refreshedUser = buildUserInfoFromTokenParsed(keycloakService.getTokenParsed());
      const tokenExpiry = getKeycloakTokenExpiry();
      const updates = getTokenStateUpdate(get(), token, tokenExpiry, refreshedUser);
      if (updates) {
        set(updates);
      }
      return token;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      set({
        isLoading: true,
        error: {
          code: 'NETWORK_ERROR' as AuthErrorCode,
          message: 'Connection lost. Reconnecting your session...',
        },
      });
      return null;
    }

    set({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      accessToken: null,
      hubspotCustomerId: null,
      tokenExpiry: null,
      entitlementsRefreshed: false,
      error: {
        code: 'TOKEN_EXPIRED' as AuthErrorCode,
        message: 'Session expired. Please sign in again.',
      },
    });
    broadcastShellLogout();

    return null;
  },

  refreshToken: async () => {
    try {
      const token = await get().ensureValidToken();
      return !!token;
    } catch (error) {
      set({
        error: {
          code: 'TOKEN_REFRESH_FAILED' as AuthErrorCode,
          message: 'Failed to refresh token',
          originalError: error,
        },
      });
      return false;
    }
  },

  setUser: (user: UserInfo) => {
    set({ user, isAuthenticated: true, hubspotCustomerId: null });
  },

  clearAuth: () => {
    set({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      accessToken: null,
      hubspotCustomerId: null,
      tokenExpiry: null,
      error: null,
      entitlementsRefreshed: false,
    });
  },
}));

export default useAuthStore;
